import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://live-event-photos.onrender.com/api';

// Get auth token from localStorage
const getToken = ( ) => localStorage.getItem('token');

// API instance with auth header
const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Transform photo data from backend format to frontend format
const transformPhoto = (photo) => ({
  ...photo,
  uploadedAt: parseInt(photo.uploadedat || photo.uploadedAt, 10), // Handle both formats
});

// Auth API - unified for all users (super admins and clients)
export const authAPI = {
  login: (data) => api.post('/admin/login', data),
  changePassword: (currentPassword, newPassword) => api.post('/admin/change-password', { currentPassword, newPassword }),
  forgotPassword: (email) => api.post('/admin/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/admin/reset-password', { token, newPassword }),
};

// Events API
export const eventsAPI = {
  create: (data) => api.post('/events', data),
  getAll: (clientId) => {
    const params = {};
    if (clientId && clientId !== 'all') {
      params.clientId = clientId;
    }
    return api.get('/admin/events', { params });
  },
  getOne: (id) => api.get(`/events/${id}`),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  getPhotos: async (id, options = {}) => {
    const params = { ...options.params }; // Start with existing params (like cache buster)
    if (options.photographerIds && options.photographerIds.length > 0) {
      params.photographerIds = options.photographerIds.join(',');
    }
    if (options.tagType && options.tagType !== 'all') {
      params.tagType = options.tagType;
    }
    if (options.activityId) {
      params.activityId = options.activityId;
    }
    console.log('🔍 getPhotos params:', params, 'options:', options);
    const { params: _, ...restOptions } = options; // Remove params from options to avoid overwriting
    const response = await api.get(`/events/${id}/photos`, { ...restOptions, params });
    // Transform photos to fix date format
    if (response.data && response.data.photos) {
      response.data.photos = response.data.photos.map(transformPhoto);
    }
    return response;
  },
  deletePhoto: (eventId, photoId) => api.delete(`/events/${eventId}/photos/${photoId}`),
  tagPhotos: (eventId, data) => api.post(`/events/${eventId}/photos/tag`, data),
  getPhotographers: (eventId, tagType) => {
    const params = {};
    if (tagType && tagType !== 'all') {
      params.tagType = tagType;
    }
    return api.get(`/events/${eventId}/photographers`, { params });
  },
  unblockPhotographer: (photographerId) => api.post(`/photographers/${photographerId}/unblock`),
  blockPhotographer: (photographerId) => api.post(`/photographers/${photographerId}/block`),
  resetPhotographerStrikes: (photographerId) => api.post(`/photographers/${photographerId}/reset-strikes`),
  cleanupEventData: (eventId) => api.post(`/events/${eventId}/cleanup-event-data`),
  cleanupOrphanedFaces: (eventId) => api.post(`/events/${eventId}/cleanup-orphaned-faces`),
  deleteUserBiometricData: (userId) => api.post(`/users/${userId}/delete-biometric-data`),
  sendNotification: (photographerId, message) => api.post(`/photographers/${photographerId}/notify`, { message }),
  sendUserNotification: (userId, message) => api.post(`/users/${userId}/notify`, { message }),
  broadcastNotification: (eventId, message, targetRole) => api.post(`/events/${eventId}/broadcast`, { message, targetRole }),
  getPhotographerRequests: (eventId) => api.get(`/events/${eventId}/photographer-requests`),
  approvePhotographer: (photographerId) => api.post(`/photographers/${photographerId}/approve`),
  convertToAttendee: (photographerId) => api.post(`/photographers/${photographerId}/convert-to-attendee`),
  convertToPhotographer: (attendeeId) => api.post(`/photographers/${attendeeId}/convert-to-photographer`),
  getThankYouStatus: (eventId) => api.get(`/events/${eventId}/thank-you-status`),
  sendThankYouSms: (eventId) => api.post(`/events/${eventId}/send-thank-you`),
  sendFullGallery: (eventId, userIds) => api.post(`/events/${eventId}/send-full-gallery`, { userIds }),
  // User Management
  getUsers: (eventId, role) => {
    const params = {};
    if (role && role !== 'all') {
      params.role = role;
    }
    return api.get(`/events/${eventId}/users`, { params });
  },
  createUser: ({ eventId, ...data }) => api.post(`/events/${eventId}/photographers`, data),
  updateUser: (userId, data) => api.put(`/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/users/${userId}`),
  importUsersCSV: (eventId, formData) => api.post(`/events/${eventId}/users/import-csv`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  exportUsersCSV: (eventId) => api.get(`/events/${eventId}/users/export-csv`, {
    responseType: 'text'
  }),
  uploadWatermark: (formData) => api.post('/events/upload-watermark', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadPhotos: (eventId, formData) => api.post(`/events/${eventId}/upload-photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadZipPhotos: (eventId, formData, onProgress) => api.post(`/events/${eventId}/upload-zip`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress({ current: progressEvent.loaded, total: progressEvent.total, percent: percentCompleted });
      }
    }
  }),
  
  // Activities
  getActivities: (eventId) => api.get(`/events/${eventId}/activities`),
  createActivity: (eventId, data) => api.post(`/events/${eventId}/activities`, data),
  updateActivity: (eventId, activityId, data) => api.put(`/events/${eventId}/activities/${activityId}`, data),
  deleteActivity: (eventId, activityId) => api.delete(`/events/${eventId}/activities/${activityId}`),
  assignPhotosToActivity: (eventId, data) => api.post(`/events/${eventId}/photos/assign-activity`, data),
  uploadActivitiesCSV: (eventId, data) => api.post(`/events/${eventId}/activities/upload-csv`, data),
};

export default api;
